import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router';
import ImportMenuButton from './ImportMenuButton';
import * as styles from './TopbarCreatePostButton.module.scss';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import Button from '../components/Button';
import Caret from '../components/Caret';
import { MenuItem, MenuSeparator } from '../components/Menu';
import OverflowMenu from '../components/OverflowMenu';
import { UserType } from '../graphql/generated/graphql';
import topicIcon from '../icons/non-prefilled/16x16/topic.svg';
import { InviteMenuItem } from '../organization/InviteButton';
import CreatePostButton from '../post/CreatePostButton';
import CreatePostMenuItem from '../post/CreatePostMenuItem';
import GoogleDrivePicker from '../search/GoogleDrivePicker';
import TemplatesMenuItem from '../templates/TemplatesMenuItem';
import NewTopicModal from '../topic/modal/NewTopicModal';
import { UIActions } from '../ui/uiSlice';

interface TopbarCreatePostOverflowMenuProps {
  trigger: React.ReactNode;
  topicId?: string;
}

const TopbarCreatePostOverflowMenu = ({
  trigger,
  topicId,
}: TopbarCreatePostOverflowMenuProps) => (
  <OverflowMenu button={trigger}>
    <CreatePostMenuItem topicId={topicId}>Create post</CreatePostMenuItem>
    <TemplatesMenuItem />
    <ImportMenuButton topicId={topicId} />
    <MenuSeparator />
    <CreateTopicMenuItem />
    <InviteMenuItem withIcon />
  </OverflowMenu>
);

interface TopbarCreatePostButtonProps {
  topicId?: string;
}

const TopbarCreatePostButton = ({ topicId }: TopbarCreatePostButtonProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const isLocked = useAppSelector(
    (state) => state.session.organization.isLocked,
  );

  useEffect(() => {
    if (searchParams.get('importFrom') === 'google') {
      const topicIds = searchParams.getAll('topicIds');
      dispatch(
        UIActions.openModal(GoogleDrivePicker, {
          topicIds,
          disableOutsideClose: true,
        }),
      );
      setSearchParams({});
    }
  });

  const dropdownButton = (
    <Button
      className={styles.dropdownButton}
      disabled={Boolean(isLocked)}
      variant="secondary"
      aria-label="More options"
    >
      <Caret direction="down" size={10} />
    </Button>
  );

  return (
    <div className={styles.container}>
      <CreatePostButton
        className={styles.mainButton}
        topicId={topicId}
        variant="secondary"
      >
        Create post
      </CreatePostButton>
      {isLocked ? (
        dropdownButton
      ) : (
        <TopbarCreatePostOverflowMenu
          trigger={dropdownButton}
          topicId={topicId}
        />
      )}
    </div>
  );
};

const CreateTopicMenuItem = () => {
  const currentUser = useAppSelector((state) => state.session.user);
  const isGuest = currentUser.type === UserType.Guest;
  const dispatch = useAppDispatch();

  if (isGuest) return null;

  return (
    <MenuItem
      onClick={() => {
        dispatch(UIActions.openModal(NewTopicModal));
      }}
      icon={topicIcon}
    >
      Create topic
    </MenuItem>
  );
};

export default TopbarCreatePostButton;
